import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import "./companyregistration.css";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import Select from 'react-select';
import Swal from "sweetalert2";
import ukCitiesRaw from '../map.json';
import debounce from "lodash.debounce";
import { FaEye, FaEyeSlash } from "react-icons/fa";


const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const SUPPORTED_FORMATS_POLICY = ['application/pdf'];
const FILE_SIZE = 10 * 1024 * 1024; // 10MB
//const API_URL = 'http://localhost:4060/api/';
//const API_URL = 'http://185.151.29.175:3000/api/';
const API_URL = 'https://api.bookinclass.co.uk/';


const schema = yup.object().shape({
  // Company Details
  company_name: yup.string().required("Company Name is required"),
  trading_name: yup.string(),
  registration_number: yup.string().when('business_type', {
    is: 'Ltd',
    then: (schema) => schema.required("Company Registration Number is required for Ltd companies"),
    otherwise: (schema) => schema.notRequired()
  }),
  vat_number: yup.string(),
  business_type: yup.string().required("Business Type is required"),
  website: yup.string().url("Must be a valid URL"),
  slug: yup.string()
  .required("Slug is required")
  .min(3, "Slug must be at least 3 characters")
  .max(50, "Slug must not exceed 50 characters")
  .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase, contain only letters, numbers, and hyphens (no spaces or special characters). Cannot start or end with a hyphen.")
  .test("notRegister", "The slug 'register' is reserved and cannot be used", (value) => {
    if (!value) return true;
    return value.toLowerCase() !== 'register';
  })
  .test("checkDuplicateSlug", "This slug is already taken", async (value, context) => {
    if (!value) return true;
    
    // Only run duplicate check if all previous validations passed
    const { path, createError } = context;
    
    // Check if value passes all format requirements first
    if (value.length < 3 || value.length > 50 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
      return true; // Let other validators handle these errors
    }
    
    try {
      const res = await fetch(`${API_URL}company/check-slug?slug=${value}`);
      const data = await res.json();
      return data.data.available;
    } catch (err) {
      return true; // fallback to pass
    }
  }),
  // Contact Info
  full_name: yup.string().required("Full Name is required"),
  job_role: yup.string().required("Job Title is required"),
  email: yup.string()
          .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, "Invalid email")
          .required("Email is required")
          .test("checkDuplicateEmail", "This email is already in use", async (value) => {
            if (!value) return true;
            try {
              const res = await fetch(`${API_URL}admin/checkEmailDuplication?email=${encodeURIComponent(value)}`);
              const data = await res.json();
              return !data.data.exists;
            } catch (err) {
              return true; // fallback to pass
            }
          }),
  phone_number: yup.string()
                .required("Phone Number is required")
                .matches(/^[0-9]+$/, "Phone number must contain only numbers")
                .test("checkDuplicatePhone", "This phone number is already taken", async (value) => {
                  if (!value) return true;
                  try {
                    const res = await fetch(`${API_URL}company/check-phone?phone_number=${value}`);
                    const data = await res.json();
                    return data.data.available;
                  } catch (err) {
                    return true; // fallback to pass
                  }
                }),

  // Address
  address_line1: yup.string().required("Address Line 1 is required"),
  address_line2: yup.string(),
  city: yup.string().required("City is required"),
  postcode: yup.string().required("Postcode is required").matches(/^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i, "Please enter a valid postcode"),
  country: yup.string().required("Country is required"),
  county: yup.string().required("County is required"),

  // Business Ops
  services: yup.array().min(1, "At least one service must be selected"),
  target_age: yup.string().required("Target age range is required"),
  no_of_weekly_bookings: yup.string().required("Estimated weekly booking number is required"),
  operating_hours: yup.string().required("Operating hours are required"),

  // Platform Prefs
  booking_cancellation_policy: yup
    .mixed()
    .nullable()
    .notRequired()
    .test("fileSize", "Booking cancellation policy must be less than 10MB", (value) => {
      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
      return value[0].size <= FILE_SIZE;
    })
    .test("fileFormat", "Unsupported file format", (value) => {
      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
      return SUPPORTED_FORMATS_POLICY.includes(value[0].type);
    }),
  pricing_structure: yup.string().required("Pricing structure is required"),
  notes: yup.string(),

  //Branding
  logo: yup
    .mixed()
    .nullable()
    .notRequired()
    .test("fileSize", "Logo must be less than 10MB", (value) => {
      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
      return value[0]?.size <= FILE_SIZE;
    })
    .test("fileFormat", "Unsupported file format", (value) => {
      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
      return SUPPORTED_FORMATS.includes(value[0]?.type);
    }),
  brand_color: yup.string().required("Brand colour is required"),

  // Terms
  confirmInfo: yup.bool().oneOf([true], "You must confirm the information is accurate"),
  agreeTerms: yup.bool().oneOf([true], "You must agree to the terms"),

  //SMTP
  smtp_host: yup.string().required("SMTP Host is required"),
  smtp_username: yup.string().required("SMTP Username is required"),
  smtp_password: yup.string().required("SMTP Password is required"),
  smtp_from_email: yup.string().required("SMTP From Email is required")
 });

 // In CompanyRegistrationForm.js

  const handleConnect = (e) => {

    e.preventDefault(); // Prevent default button behavior
    //const clientId = 'ca_SGzm6VMcyZ0XZGiCWrIafVN5WCZLI0ZS'; // from Stripe Dashboard
    const clientId = 'ca_RoFhJux5fA8OehUUCuA5iy3D1k2bVa8Q'; // from Stripe Dashboard
    
    //const redirectUri = encodeURIComponent('http://localhost:3000/bookinclass-user/company-registration');
    //const redirectUri = encodeURIComponent('http://dev.register.bookinclass.co.uk/company');
    const redirectUri = encodeURIComponent('https://register.bookinclass.co.uk/company');
    const state = 'bic_1234';

    const connectUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${redirectUri}&state=${state}`;

    window.location.href = connectUrl;
  };


function CompanyRegistration() {
  const [submitting, setSubmitting] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [stripeError, setStripeError] = useState('');
  const [SlugValue, setSlugValue] = useState("");
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    reset,
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
  });

  const onInvalid = (errors) => {
    if (!stripeAccountId) {
      setStripeError('Please connect your Stripe account before submitting the form')
      return;
    }
  };

  const onSubmit = async (data) => {

    // if (SlugValue == null) {
    //   setSlugAvailable(false);
    //   return;
    // }

    let hasErrors = false;

    if (!stripeAccountId) {
      setStripeError('Please connect your Stripe account before submitting the form')
      hasErrors = true;
    }

    if (!logoBase64) {
      setError('logo', {
        type: 'manual',
        message: 'Please upload logo',
      });
      hasErrors = true;
    }

    if (!bookingCancellationPolicyBase64) {
      setError('booking_cancellation_policy', {
        type: 'manual',
        message: 'Please upload booking cancellation policy',
      });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    console.log("Submitting...", data);
    setSubmitting(true);

    const formData = new FormData();
    
    // Convert base64 to file for logo
    if (logoBase64) {
      const logoBlob = await fetch(logoBase64).then(res => res.blob());
      const logoFile = new File([logoBlob], 'logo.png', { type: 'image/png' });
      formData.append("logo", logoFile);
    }

    // Convert base64 to file for banner
    if (bannerBase64) {
      const bannerBlob = await fetch(bannerBase64).then(res => res.blob());
      const bannerFile = new File([bannerBlob], 'banner.png', { type: 'image/png' });
      formData.append("banner", bannerFile);
    }

    // Convert base64 to file for booking cancellation policy
    if (bookingCancellationPolicyBase64) {
      const policyBlob = await fetch(bookingCancellationPolicyBase64).then(res => res.blob());
      const policyFile = new File([policyBlob], policyFileName || 'policy.pdf', { type: 'application/pdf' });
      formData.append("booking_cancellation_policy", policyFile);
    }
    
    if (data['city'] === "manual") {
      data['city'] = manualCity;
    }

    // Add other form data
    for (const key in data) {
      if (key === "services") {
        formData.append("services", JSON.stringify(data[key]));
      } else if (key !== "logo" && key !== "banner" && key !== "booking_cancellation_policy") {
        formData.append(key, data[key]);
      }
    }


    formData.append("stripe_account_id", stripeAccountId);

    try {
      const response = await axios.post(`${API_URL}company/registration`, formData);
      console.log('Response:', JSON.stringify(response.data, null, 2));
      
      if(response.data.data.status === 1){
        localStorage.removeItem("companyForm");
        localStorage.removeItem("logoBase64");
        localStorage.removeItem("bannerBase64");
        localStorage.removeItem("bookingCancellationPolicyBase64");
        localStorage.removeItem("policyFileName");
        reset();
        Swal.fire({
          icon: "success",
          text: 'Application successfully submitted!',
          timer: 3000,
          showConfirmButton: false
        });
        
        setTimeout(() => {
          window.location.href = "company/thank-you";
        }, 3000);
      }else{
        Swal.fire("Opps!","Something Went Wrong!","error");
      }
    } catch (err) {
      console.error(err);
      alert("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

   // Debounced API call function
   const checkSlugAvailability = debounce(async (value) => {
    if (!value) return;

    // Check if slug is "register" (reserved word)
    if (value.toLowerCase() === 'register') {
      setSlugAvailable(false);
      return;
    }

    setChecking(true);
    try {
      const response = await fetch(`${API_URL}company/check-slug?slug=${value}`);
      const data = await response.json();
      setSlugAvailable(data.data.available); // assuming API returns { available: true/false }
      if(data.data.available){
        setSlugValue(value);
      }
    } catch (error) {
      console.error("Slug check error:", error);
      setSlugAvailable(null);
    } finally {
      setChecking(false);
    }
  }, 500); // 500ms debounce

  const handleSlug = async (e) => {
    const value = e.target.value.trim().toLowerCase();
    await trigger("slug");
    checkSlugAvailability(value);
  };

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        checkSlugAvailability.cancel();
      };
    }, []);


    const handleEmail = async (e) => {
      const value = e.target.value.trim();
      await trigger("email");
      try {
        const response = await fetch(`${API_URL}company/check-email?email=${value}`);
        const data = await response.json();
        if(!data.data.available){
          setError('email', {
            type: 'manual',
            message: 'This email is already taken'
          });
        }else{
          //clearErrors('email');
        }
      } catch (error) {
        console.error("Email check error:", error);
      }
    };

    const handlePhoneNumber = async (e) => {
      const value = e.target.value.trim();
      await trigger("phone_number");
      try {
        const response = await fetch(`${API_URL}company/check-phone?phone_number=${value}`);
        const data = await response.json();
        if(!data.data.available){
          setError('phone_number', {
            type: 'manual',
            message: 'This phone number is already taken'
          });
        }else{
          //clearErrors('phone_number');
        }
      } catch (error) {
        console.error("Phone check error:", error);
      }
    };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const returnedState = params.get("state");
  
    if (code) {
      Swal.fire({
        title: 'Please wait...',
        text: 'We are fetching your Stripe account details',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      fetch(`${API_URL}company/stripe/exchange-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.data.stripe_account_id) {
            setStripeAccountId(data.data.stripe_account_id);
            setStripeError('')
            window.history.replaceState(null, '', window.location.pathname);
            Swal.close();
            Swal.fire({
              title: 'Success!',
              text: 'Stripe account connected successfully',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
          }else{
            Swal.close();
            Swal.fire({
              title: 'Error!',
              text: 'Failed to connect Stripe account. Please try again.',
              icon: 'error'
            });
          }
        })
        .catch((error) => {
          Swal.close();
          Swal.fire({
            title: 'Error!',
            text: 'Failed to connect Stripe account. Please try again.',
            icon: 'error'
          });
        });
    }
  }, []);

   // Load saved data on mount
   useEffect(() => {
    const saved = localStorage.getItem("companyForm");
    if (saved) {
      const data = JSON.parse(saved);
      Object.keys(data).forEach((key) => {
        setValue(key, data[key]);
      });
    }
  }, [setValue]);

  // Watch and save form data to localStorage
  const watchedValues = watch();
  useEffect(() => {
    localStorage.setItem("companyForm", JSON.stringify(watchedValues));
  }, [watchedValues]);


  const [countyCityMap, setCountyCityMap] = useState({});
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const countries = ['UK'];

  // First load the county map
  useEffect(() => {
    const map = {};
    ukCitiesRaw.forEach(entry => {
      const city = entry[0];
      const county = entry[5] || "Unknown";
      if (!map[county]) {
        map[county] = [];
      }
      if (!map[county].includes(city)) {
        map[county].push(city);
      }
    });
    setCountyCityMap(map);
  }, []);

  // Then restore form data after map is loaded
  useEffect(() => {
    if (Object.keys(countyCityMap).length > 0) {
      const saved = localStorage.getItem("companyForm");
      if (saved) {
        const data = JSON.parse(saved);
        if (data.county) {
          setSelectedCounty(data.county);
          setValue("county", data.county);
        }
        if (data.city) {
          const cityList = countyCityMap[data.county] || [];
          const isManualCity = !cityList.includes(data.city);

          if (isManualCity) {
            setSelectedCity('');
            setShowManualInput(true);
            setManualCity(data.city);
          } else {
            setSelectedCity(data.city);
            setShowManualInput(false);
            setManualCity('');
          }

          setValue("city", data.city);
        }
      }
    }
  }, [countyCityMap, setValue]);

  const handleCountyChange = (e) => {
    const newCounty = e.target.value;
    setSelectedCounty(newCounty);
    setValue("county", newCounty, { shouldValidate: true, shouldDirty: true });
    setSelectedCity('');
    setManualCity('');
    setShowManualInput(false);
    setValue("city", '', { shouldValidate: true, shouldDirty: true });
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    if (value === 'manual') {
      setSelectedCity('');
      setShowManualInput(true);
      setValue("city", '', { shouldDirty: true });
    } else {
      setSelectedCity(value);
      setShowManualInput(false);
      setManualCity('');
      setValue("city", value, { shouldValidate: true, shouldDirty: true });
    }
  };
  
  const handleWebsiteChange = (e) => {
    let value = e.target.value;
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      value = 'https://' + value;
    }
    setValue('website', value);
  };

const [logoBase64, setLogoBase64] = useState(() => localStorage.getItem('logoBase64') || '');
const [bannerBase64, setBannerBase64] = useState(() => localStorage.getItem('bannerBase64') || '');
const [bookingCancellationPolicyBase64, setBookingCancellationPolicyBase64] = useState(() =>
  localStorage.getItem('bookingCancellationPolicyBase64') || ''
);
const [policyFileName, setPolicyFileName] = useState(() => localStorage.getItem('policyFileName') || '');



  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setLogoBase64(reader.result);      // Base64 string (including the data:image/... prefix)
      localStorage.setItem('logoBase64', reader.result);
    };

    reader.readAsDataURL(file); // Converts the file to base64
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setBannerBase64(reader.result);      // Base64 string (including the data:image/... prefix)
      localStorage.setItem('bannerBase64', reader.result);
    };

    reader.readAsDataURL(file); // Converts the file to base64
  };

  const handleBookingCancellationPolicyChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      setBookingCancellationPolicyBase64(reader.result);
      setPolicyFileName(file.name);
      localStorage.setItem('bookingCancellationPolicyBase64', reader.result);
      localStorage.setItem('policyFileName', file.name);
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="company-form-container wide">
      <h1><center>Company Registration</center></h1>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} encType="multipart/form-data" className="company-form">
        {/* === Company Details === */}
        <h3 className="form-section-title">Company Details</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Company Name *</label>
            <input {...register("company_name")} />
            <p className="error">{errors.company_name?.message}</p>
          </div>
          <div className="form-group">
            <label>Trading Name (if different)</label>
            <input {...register("trading_name")} />
          </div>
          <div className="form-group">
            <label>VAT Number <span className="text-muted">(If applicable)</span></label>
            <input {...register("vat_number")} />
          </div>
        </div>
        <div className="form-grid">
          
          <div className="form-group">
            <label>Business Type *</label>
            <select {...register("business_type")}>
              <option value="">Select</option>
              <option value="Ltd">Ltd</option>
              <option value="Sole Trader">Sole Trader</option>
              <option value="Charity">Charity</option>
            </select>
            <p className="error">{errors.business_type?.message}</p>
          </div>
          <div className="form-group">
            <label>Registration Number <span className="text-muted">(If applicable)</span></label>
            <input {...register("registration_number")} />
            <p className="error">{errors.registration_number?.message}</p>
          </div>
          <div className="form-group">
            <label>Website</label>
            <input 
              {...register("website")} 
              onChange={handleWebsiteChange}
              placeholder="example.com"
            />
            <p className="error">{errors.website?.message}</p>
          </div>
        </div>

        <div className="form-group">
            <label>URL ID *</label>
            <div style={{ 
              display: "flex", 
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center", 
              gap: "10px",
              width: "50%"
            }}>
              <div style={{ flex: "1", minWidth: "200px" }}>
                <input 
                  {...register("slug")} 
                  //onBlur={handleSlug}
                  placeholder=""
                  style={{ width: "40%" }}
                /><span> .bookinclass.co.uk</span>
              </div>
              <div style={{ 
                flex: "0 0 auto",
                minWidth: "150px",
                display: "flex",
                alignItems: "left"
              }}>
                {checking ? (
                  <p style={{ color: "orange", margin: 0 }}>Checking availability...</p>
                ) : slugAvailable === true ? (
                  <p style={{ color: "green", margin: 0 }}>✅ URL ID is available</p>
                ) : slugAvailable === false ? (
                  <p style={{ color: "red", margin: 0 }}>❌ URL ID is already taken</p>
                ) : null}
              </div>
            </div>
            <p className="error">{errors.slug?.message}</p>
            {slugAvailable === true && (
              <div style={{ 
                marginTop: "8px",
                padding: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                display:"none"
              }}>
                <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>Preview:</p>
                <p style={{ margin: "0 0 5px 0" }}>https://{SlugValue}.bookinclass.co.uk</p>
                <p style={{ margin: 0 }}>https://{SlugValue}.admin.bookinclass.co.uk</p>
              </div>
            )}
          </div>

        <h3 className="form-section-title">Stripe Connect *</h3>
        <div className="form-grid">
          <button 
            onClick={handleConnect} 
            className="submit-btn" 
            style={{ float: 'left', marginTop: '0px', marginLeft: '0px' }}
            disabled={stripeAccountId !== null}
          >
            {stripeAccountId ? 'Stripe Connected' : 'Connect with Stripe'}
          </button>  
          <p className="error" style={{ marginLeft: '10px', marginTop: '5px' }}>
            {stripeError}
          </p>          
        </div>

        {/* === Contact Info === */}
        <h3 className="form-section-title">Primary Contact Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Full Name *</label>
            <input {...register("full_name")} />
            <p className="error">{errors.full_name?.message}</p>
          </div>
          <div className="form-group">
            <label>Job Title *</label>
            <input {...register("job_role")} />
            <p className="error">{errors.job_role?.message}</p>
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input {...register("email")} onBlur={handleEmail}/>
            <p className="error">{errors.email?.message}</p>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>Phone Number *</label>
            <input {...register("phone_number")} onBlur={handlePhoneNumber} />
            <p className="error">{errors.phone_number?.message}</p>
          </div>
        </div>

        {/* === Address === */}
        <h3 className="form-section-title">Business Address</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Address Line 1 *</label>
            <input {...register("address_line1")} />
            <p className="error">{errors.address_line1?.message}</p>
          </div>
         
          <div className="form-group">
            <label>Address Line 2</label>
            <input {...register("address_line2")} />
          </div>
          <div className="form-group">
            <label>County*</label>
            <select 
              {...register("county")} 
              value={selectedCounty} 
              onChange={handleCountyChange}
            >
              <option value="">Select County</option>
              {Object.keys(countyCityMap).sort().map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
            <p className="error">{errors.county?.message}</p>
          </div>
          
        </div>
        <div className="form-grid">
        <div className="form-group">
            <label>Town/City *</label>
            <select 
              {...register("city")} 
              value={selectedCity || (showManualInput ? "manual" : "")} 
              onChange={handleCityChange}
            >
              <option value="">Select City/Town</option>
              {selectedCounty && countyCityMap[selectedCounty]?.sort().map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
              <option value="manual">My town/village isn't listed</option>
            </select>
            {showManualInput && (
              <>
                <label>Enter City/Town:</label>
                <input
                  type="text"
                  placeholder="Enter your town or village"
                  value={manualCity}
                  onChange={(e) => {
                    const value = e.target.value;
                    setManualCity(value);
                    setValue("city", value, { shouldValidate: true, shouldDirty: true });
                  }}
                />
              </>
            )}
            <p className="error">{errors.city?.message}</p>
          </div>

          <div className="form-group">
            <label>Country*</label>
            <select {...register("country")}>
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            <p className="error">{errors.country?.message}</p>
          </div>
          <div className="form-group">
            <label>Postcode *</label>
            <input {...register("postcode")} />
            <p className="error">{errors.postcode?.message}</p>
          </div>
        </div>

        {/* === Business Operations === */}
        <h3 className="form-section-title">Business Operations</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Services Offered *</label>
            <Controller
              name="services"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  isMulti
                  options={[
                    { value: "Martial Arts", label: "Martial Arts" },
                    { value: "Holiday Clubs", label: "Holiday Clubs" },
                    { value: "Tuition", label: "Tuition" },
                    { value: "Sports Coaching", label: "Sports Coaching" },
                    { value: "After School Clubs", label: "After School Clubs" },
                    { value: "Other", label: "Other" }
                  ]}
                  className="select2-container"
                  classNamePrefix="select2"
                  placeholder="Select services..."
                  value={(field.value || []).map(service => ({ value: service, label: service }))}
                  onChange={(selectedOptions) => {
                    const values = (selectedOptions || []).map(option => option.value);
                    field.onChange(values);
                    trigger("services");
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
            <p className="error">{errors.services?.message}</p>
          </div>
          <div className="form-group">
            <label>Target Age Range *</label>
            <input {...register("target_age")} />
            <p className="error">{errors.target_age?.message}</p>
          </div>
          <div className="form-group">
            <label>Estimated Weekly Bookings *</label>
            <input {...register("no_of_weekly_bookings")} />
            <p className="error">{errors.no_of_weekly_bookings?.message}</p>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>Typical Operating Hours *</label>
            <input {...register("operating_hours")} />
            <p className="error">{errors.operating_hours?.message}</p>
          </div>
        </div>

        {/* === Platform Preferences === */}
        <h3 className="form-section-title">Platform Preferences</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Booking Cancellation Policy *</label>
            <input type="file" {...register("booking_cancellation_policy")} onChange={handleBookingCancellationPolicyChange} accept="application/pdf"/>
            {policyFileName && (
              <p className="text-muted mt-1">Selected file: {policyFileName}</p>
            )}
            <p className="error">{errors.booking_cancellation_policy?.message}</p>
          </div>
          <div className="form-group">
            <label>Custom Features or Notes</label>
            <textarea rows="5" {...register("notes")} />
          </div>
          <div className="form-group">
            <label>Pricing Structure *</label>
            <select {...register("pricing_structure")}>
              <option value="">-- Select a Pricing Structure --</option>
                <option value="Flate Rate">Flate Rate</option>
                <option value="Per Session">Per Session</option>
                <option value="Tiered">Tiered</option>
              </select>
            <p className="error">{errors.pricing_structure?.message}</p>
          </div>
          
        </div>

        {/* === Branding === */}
        <h3 className="form-section-title">Branding Assets</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Upload Logo *</label>
            <input type="file" {...register("logo")} onChange={handleLogoChange}/>
            {logoBase64 && (
              <div style={{ 
                marginTop: '10px', 
                border: '1px solid #ccc', 
                padding: '10px', 
                width: '100px', 
                height: '100px', 
                backgroundImage: `url(${logoBase64})`, 
                backgroundSize: 'cover', 
                backgroundPosition: 'center', 
                backgroundRepeat: 'no-repeat',
                borderRadius: '8px'
              }}>
              </div>
            )}
            <p className="error">{errors.logo?.message}</p>
          </div>
          <div className="form-group">
            <label>Brand Colour * (HEX Code)</label>
            <input type="text" {...register("brand_color")} />
            <p className="error">{errors.brand_color?.message}</p>
          </div>
          <div className="form-group">
            <label>Banner Image <span className="text-muted">(Recommended ratio: 3:1)</span></label>
            <input type="file" {...register("banner")} onChange={handleBannerChange}/>
            {bannerBase64 && (
               <div style={{ 
                marginTop: '10px', 
                border: '1px solid #ccc', 
                padding: '10px', 
                width: '100px', 
                height: '100px', 
                backgroundImage: `url(${bannerBase64})`, 
                backgroundSize: 'cover', 
                backgroundPosition: 'center', 
                backgroundRepeat: 'no-repeat',
                borderRadius: '8px'
              }}>
              </div>
            )}
          </div>
        </div>

        {/* === SMTP Configuration === */}
        <h3 className="form-section-title">SMTP Configuration</h3>
        <div className="form-grid">
        <div className="form-group">
            <label>SMTP Host*</label>
            <input type="text" {...register("smtp_host")} />
            <p className="error">{errors.smtp_host?.message}</p>
          </div>
          <div className="form-group">
            <label>SMTP Username*</label>
            <input type="text" {...register("smtp_username")} />
            <p className="error">{errors.smtp_username?.message}</p>
          </div>
          {/* <div className="form-group">
            <label>SMTP Password*</label>
            <input type="password" {...register("smtp_password")} />
            <p className="error">{errors.smtp_password?.message}</p>
          </div> */}
          <div className="form-group" style={{ position: "relative" }}>
            <label>SMTP Password*</label>
            <input
              type={showPassword ? "text" : "password"}
              {...register("smtp_password")}
            />
            <span
              onClick={togglePasswordVisibility}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: "1.2rem",
                color: "#666"
              }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            <p className="error">{errors.smtp_password?.message}</p>
          </div>
          <div className="form-group">
            <label>SMTP From Email*</label>
            <input type="text" {...register("smtp_from_email")} />
            <p className="error">{errors.smtp_from_email?.message}</p>
          </div>
        </div>

        
        {/* === Terms === */}
        <div className="form-section checkbox">
          <label>
            <input type="checkbox" {...register("confirmInfo")} /> I confirm the
            information above is accurate.
          </label>
          <p className="error">{errors.confirmInfo?.message}</p>

          <label>
            <input type="checkbox" {...register("agreeTerms")} /> I agree to the
            <a href="https://samasoutheast.bookinclass.co.uk/terms-and-condition/company%20registration" target="_blank" rel="noopener noreferrer"> BookinClass platform terms and conditions</a>.
          </label>
          <p className="error">{errors.agreeTerms?.message}</p>
        </div>        

        {/* === Submit === */}
        <button type="submit" className="submit-btn">Submit Application</button>
      </form>
    </div>
  );
}

export default CompanyRegistration;
